import type { TResolution, TResolutionsLimits } from '@streaming/doppio-player';
import {
  bind,
  EPlaybackState,
  EPlayerType,
  EventBus,
  EVideoControllerCustomEvent,
  IVideoElementControllerProps,
  ObjectDeepDiff,
  TCustomEventsMap,
  TLogLevel,
  VideoElementController,
} from '@streaming/mouflon-utils';
import { isEqual, merge } from 'lodash-es';

import {
  AVAILABLE_PLAYER_TYPES,
  IStreamConfig,
  TAdditionalFields,
  TPlayerConfigPartial,
} from '@customTypes/common';
import { MetricsCollector } from '@metrics/Metrics';
import { IMetricsEventsMap } from '@metrics/Metrics.events';
import { VideoElementMetrics } from '@metrics/VideoElementMetrics';
import { type DoppioWrapper } from '@players/Doppio';
import { TPossiblePlayerWrapper, TPossiblePlayerWrapperConstructor } from '@players/IPlayers';
import { type NativeWrapper } from '@players/Native';
import { EPlayersEvents, IPlayersEventsMap, TPlayersEventsPayload } from '@players/Players.events';
import { type RistrettoBroadcastWrapper } from '@players/RistrettoBroadcasting';
import { type RistrettoPlayerWrapper, type TWebRTCAbrPreset } from '@players/RistrettoPlaying';
import { TMMPEventBus } from '@utils/EventBus';
import { playerTypeChecker } from '@utils/PlayerTypeChecker';
import { getPlayerTypesByStreamConfig } from '@utils/getPlayerTypesByStreamConfig';

import { EModuleLoadingState, EMouflonPlayerEvent } from './Player.events';
import { defaultMouflonPlayerConfig } from './PlayerConfig';
import { PlayerEventEmitter } from './PlayerEventEmitter';
import { DebugPanel } from '../DebugPanel/DebugPanel';
import { MMP_VERSION } from '../version';

type TParsedPlaylist = Partial<{
  streamId: string;
  resolution: string;
}>;

const sleep: (ms: number) => Promise<void> = (ms) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

export const parsePlaylistUrl: (url: string) => TParsedPlaylist = (url) => {
  if (!url) {
    return {};
  }

  try {
    const urlObj: URL = new URL(url);
    const pathParts: string[] = urlObj.pathname.split('/');
    const streamId: string = pathParts[2];
    const fileName: string = pathParts[pathParts.length - 1];
    const resolution: string = fileName.match(/\d+_(\w+)/)?.[1] || 'source';

    return { streamId, resolution } as TParsedPlaylist;
  } catch (e) {
    return {};
  }
};

export class MouflonPlayer {
  private _doppioWrapperConstructor: typeof DoppioWrapper | null = null;
  private _videoElement: HTMLVideoElement | null = null;
  private _ristrettoPlayerWrapperConstructor: typeof RistrettoPlayerWrapper | null = null;
  private _ristrettoBroadcasterWrapperConstructor: typeof RistrettoBroadcastWrapper | null = null;
  private _nativeWrapperConstructor: typeof NativeWrapper | null = null;
  private _supportedPlayerTypeCache: EPlayerType[] | null = null;
  private _currentPlayerType: EPlayerType | null = null;
  private _currentPlayerWrapperInstance: TPossiblePlayerWrapper | null = null;
  private _currentStreamConfig: IStreamConfig = {};
  private _isAbrEnabled: boolean = false;
  private _isStartDelayed: boolean = false;
  private _currentResolution: string | null = null;
  private _currentResolutionBitrate: number | null = null;
  private _metricsUserIdCache: number | string | null = null;
  private _metricsTimeOriginCache: number | null = null;
  private _abrResolutions: TWebRTCAbrPreset[] = [];
  private _config: TPlayerConfigPartial = { ...defaultMouflonPlayerConfig };
  private _appKey: string = '';
  private _metricsCollector: MetricsCollector | null = null;
  private _videoElementMetrics: VideoElementMetrics | null = null;
  private _logLevels: TLogLevel[] = [];
  private _isDestroyed: boolean = false;
  private _hasStarted: boolean = false;
  private _abTestsCache: Record<string, string> = {};
  private _abTestsSupportCache: Record<string, boolean> = {};
  private _additionalFieldsCache: TAdditionalFields = {};
  private _playerInstancesMap: Partial<Record<EPlayerType, TPossiblePlayerWrapper>> = {};
  private _videoElementController: VideoElementController;
  private _userTypeCache: 'user' | 'model' | 'unknown' = 'unknown';
  private _resolutionsCache: TResolution[] = [];
  private _resolutionLimitsCache: TResolutionsLimits = null;

  private readonly _eventEmitter: PlayerEventEmitter;
  private readonly _eventBusInstance: TMMPEventBus = new EventBus<
    IPlayersEventsMap & IMetricsEventsMap
  >();
  private readonly _playerWrapperResolversMap: Record<
    EPlayerType,
    () => Promise<TPossiblePlayerWrapperConstructor>
  > = {
    [EPlayerType.HLS]: this._resolveDoppioWrapper,
    [EPlayerType.WEBRTC]: this._resolveRistrettoWrapper,
    [EPlayerType.NATIVE]: this._resolveNativeWrapper,
    [EPlayerType.BROADCAST]: this._resolveRistrettoBroadcasterWrapper,
  };
  private readonly _debugPanel: DebugPanel = new DebugPanel(this._eventBusInstance);
  private readonly _playerTypeForcedListener: string;
  private readonly _streamConfigUpdatedInternalListener: string;
  private readonly _onResolutionsAvailableListener: string;
  private readonly _onLatencyEstimationAvailableListener: string;

  constructor() {
    this._eventEmitter = new PlayerEventEmitter(this._eventBusInstance);
    this._videoElementController = new VideoElementController();

    if (localStorage && localStorage.getItem('__mmpLoggerLevel__')) {
      this._logLevels = (localStorage.getItem('__mmpLoggerLevel__') || '').split(
        ',',
      ) as TLogLevel[];

      this._videoElementController.setLoggerLevels(this._logLevels);
    }

    if (this._config.videoControllerConfig) {
      this._videoElementController.updateConfig(this._config.videoControllerConfig);
    }

    this._videoElementController.addEventListener(
      EVideoControllerCustomEvent.VIDEO_PLAYBACK_STATE_CHANGE,
      this._handleVideoPlaybackStateChange,
    );
    this._videoElementController.addEventListener(
      EVideoControllerCustomEvent.UPDATE_PLAYER_RESOLUTION,
      this._handleUpdatePlayerResolution,
    );
    this._videoElementController.addEventListener(
      EVideoControllerCustomEvent.NATIVE_EVENT,
      this._handleNativeEvent,
    );
    this._videoElementController.addEventListener(
      EVideoControllerCustomEvent.TIME_UPDATE,
      this._handleTimeUpdate,
    );
    this._videoElementController.addEventListener(
      EVideoControllerCustomEvent.PLAYING,
      this._handlePlaying,
    );

    this._playerTypeForcedListener = this._eventBusInstance.subscribe(
      EPlayersEvents.FORCE_PLAYER_TYPE,
      this._onPlayerTypeForced,
    );

    this._streamConfigUpdatedInternalListener = this._eventBusInstance.subscribe(
      EPlayersEvents.STREAM_CONFIG_UPDATED_INTERNAL,
      this._updateStreamConfigInternal,
    );

    this._onLatencyEstimationAvailableListener = this._eventBusInstance.subscribe(
      EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE,
      this._onLatencyEstimationAvailable,
    );

    this._onResolutionsAvailableListener = this._eventBusInstance.subscribe(
      EPlayersEvents.RESOLUTIONS_AVAILABLE,
      this._onResolutionsAvailable,
    );
  }

  get instanceVersion(): string | undefined {
    return `${this._currentPlayerType} ${this._currentPlayerWrapperInstance?.version}`;
  }

  get version(): string {
    return MMP_VERSION;
  }

  public on(
    ev: EMouflonPlayerEvent,
    cb: Parameters<typeof this._eventEmitter.addEventListener>[1],
    options?: Parameters<typeof this._eventEmitter.addEventListener>[2],
  ): void {
    this._eventEmitter.addEventListener(ev, cb, options);
  }

  public off(
    ev: EMouflonPlayerEvent,
    cb: Parameters<typeof this._eventEmitter.removeEventListener>[1],
    options?: Parameters<typeof this._eventEmitter.removeEventListener>[2],
  ): void {
    this._eventEmitter.removeEventListener(ev, cb, options);
  }

  public once(
    ev: EMouflonPlayerEvent,
    cb: Parameters<typeof this._eventEmitter.removeEventListener>[1],
    options?: Parameters<typeof this._eventEmitter.removeEventListener>[2],
  ): void {
    const useCapture: boolean = typeof options === 'boolean' && options;
    const listenerOptions: AddEventListenerOptions =
      typeof options === 'object'
        ? {
            ...options,
            once: true,
          }
        : {
            once: true,
            capture: useCapture,
          };
    this._eventEmitter.addEventListener(ev, cb, listenerOptions);
  }

  public sendCustomEvent(eventName: string): void {
    this._metricsCollector?.registerEvent('CustomEvent', { customEventName: eventName });
  }

  public destroy(): void {
    this._isDestroyed = true;
    this._hasStarted = false;
    this._isStartDelayed = false;
    this._currentPlayerWrapperInstance?.destroy();
    this._eventEmitter.destroy();
    this._debugPanel.destroy();
    this._metricsCollector?.destroy();
    this._videoElementMetrics?.destroy();
    this._videoElementController.removeEventListener(
      EVideoControllerCustomEvent.VIDEO_PLAYBACK_STATE_CHANGE,
      this._handleVideoPlaybackStateChange,
    );
    this._videoElementController.removeEventListener(
      EVideoControllerCustomEvent.UPDATE_PLAYER_RESOLUTION,
      this._handleUpdatePlayerResolution,
    );
    this._videoElementController.removeEventListener(
      EVideoControllerCustomEvent.NATIVE_EVENT,
      this._handleNativeEvent,
    );
    this._videoElementController.removeEventListener(
      EVideoControllerCustomEvent.TIME_UPDATE,
      this._handleTimeUpdate,
    );

    this._videoElementController.destroy();

    this._eventBusInstance.unsubscribe(this._playerTypeForcedListener);
    this._eventBusInstance.unsubscribe(this._streamConfigUpdatedInternalListener);
    this._eventBusInstance.unsubscribe(this._onResolutionsAvailableListener);
    this._eventBusInstance.unsubscribe(this._onLatencyEstimationAvailableListener);
  }

  public enableAbr(): void {
    this._isAbrEnabled = true;
    this._currentPlayerWrapperInstance?.enableAbr();
    this._metricsCollector?.setRenditionSwitchMode('auto');
    this._videoElementMetrics?.setUserSelectedResolution(null);
  }

  public disableAbr(): void {
    this._isAbrEnabled = false;
    this._currentPlayerWrapperInstance?.disableAbr();

    this._metricsCollector?.setRenditionSwitchMode('manual');
  }

  public setMetricsUserId(userId: string | number): void {
    this._metricsCollector?.setUserId(userId);
    this._metricsUserIdCache = userId;
  }

  public setMetricsTimeOrigin(timeOrigin: number): void {
    this._metricsTimeOriginCache = timeOrigin;
    this._metricsCollector?.setTimeOrigin(timeOrigin);
  }

  public deconfigureMetrics(): void {
    this._videoElementMetrics?.destroy();
    this._videoElementMetrics = null;

    this._metricsCollector?.destroy();
    this._metricsCollector = null;
  }

  public configureMetrics(baseUrl: string, projectId: string): void {
    if (this._metricsCollector) {
      if (
        this._metricsCollector.baseUrl === baseUrl &&
        this._metricsCollector.projectId === projectId
      ) {
        return;
      }

      this._videoElementMetrics?.destroy();
      this._videoElementMetrics = null;

      this._metricsCollector.destroy();
      this._metricsCollector = null;
    }

    this._metricsCollector = new MetricsCollector(baseUrl, projectId, this._eventBusInstance);
    this._metricsCollector.setAbTests(this._abTestsCache);
    if (this._currentPlayerType) {
      this._metricsCollector.setPlayerType(this._currentPlayerType);
    }
    this._metricsCollector.setAdditionalFields(this._additionalFieldsCache);
    this._videoElementMetrics = new VideoElementMetrics(
      this._metricsCollector,
      this._eventBusInstance,
    );

    this._videoElementMetrics.setIsPaused(this._currentPlayerType === EPlayerType.BROADCAST);

    this._currentPlayerWrapperInstance?.setMetricsCollector(this._metricsCollector);

    if (this._videoElement) {
      this._videoElementMetrics.setVideoElement(this._videoElement);
    }

    this._videoElementMetrics.setResolutions(this._resolutionsCache);
    if (this._currentResolution && !this._isAbrEnabled) {
      this._videoElementMetrics.setUserSelectedResolution(this._currentResolution);
    } else {
      this._videoElementMetrics.setUserSelectedResolution(null);
    }

    if (this._metricsTimeOriginCache) {
      this._metricsCollector.setTimeOrigin(this._metricsTimeOriginCache);
    }

    if (this._metricsUserIdCache) {
      this._metricsCollector.setUserId(this._metricsUserIdCache);
    }

    if (this._userTypeCache !== 'unknown') {
      this._metricsCollector.setUserType(this._userTypeCache);
    }

    this._reemitCDNDomain();
  }

  public setConfig(config: TPlayerConfigPartial): void {
    const mergedConfig: TPlayerConfigPartial = merge({}, this._config, config);
    if (isEqual(mergedConfig, this._config)) {
      return;
    }

    const diff: TPlayerConfigPartial = ObjectDeepDiff(this._config, mergedConfig);

    this._config = { ...mergedConfig };

    if (diff.hlsConfig || diff.webRTCConfig || diff.nativeConfig) {
      this._reapplyPlayerConfig();
    }

    if (diff.videoControllerConfig && this._config.videoControllerConfig) {
      this._videoElementController.updateConfig(
        this._config.videoControllerConfig as IVideoElementControllerProps,
      );
    }

    if (diff.persistent) {
      this._prepareDependencies();
    } else if (diff.persistent === false) {
      this._clearDependencies();
    }
  }

  public setVideoElement(videoElement: HTMLVideoElement | null): void {
    this._videoElement = videoElement;
    this._eventBusInstance.publish(EPlayersEvents.VIDEO_ELEMENT_UPDATED, {
      videoElement,
    });
    if (this._videoElementController && videoElement) {
      this._videoElementController.setVideoElement(videoElement);
    }

    this._videoElementMetrics?.setVideoElement(videoElement);

    this._currentPlayerWrapperInstance?.setVideoElement(this._videoElement);

    if (this._currentPlayerType === EPlayerType.BROADCAST) {
      this.setVolume(0);
    }
  }

  public setStreamConfig(config: IStreamConfig): void {
    const updatedStreamConfig: IStreamConfig = {
      ...this._currentStreamConfig,
      ...config,
    };

    this._updateStreamConfigInternal({ streamConfig: updatedStreamConfig });
  }

  public setAbTests(abTests?: Record<string, string>): void {
    this._abTestsCache = abTests || {};
    this._metricsCollector?.setAbTests(abTests || {});
  }

  public setAbTestsSupport(abTestsSupport?: Record<string, boolean>): void {
    this._abTestsSupportCache = abTestsSupport || {};
    this._metricsCollector?.setAbTestsSupport(this._abTestsSupportCache);
  }

  public setAdditionalMetricsFields(additionalFields: TAdditionalFields): void {
    this._additionalFieldsCache = additionalFields || {};
    this._metricsCollector?.setAdditionalFields(additionalFields);
  }

  public setUserType(userType: 'model' | 'user'): void {
    this._userTypeCache = userType;
    this._metricsCollector?.setUserType(userType);
  }

  public setResolutionByName(resolutionName: string): void {
    this._currentResolutionBitrate = null;
    this._currentResolution = resolutionName;
    this._currentPlayerWrapperInstance?.setResolutionByName(resolutionName);

    if (this._metricsCollector?.currentRenditionSelectMode === 'manual') {
      this._metricsCollector.setManualResolution(resolutionName);
      this._videoElementMetrics?.setUserSelectedResolution(resolutionName);
    }
  }

  public setResolutionByBitrate(bitrate: number): void {
    this._currentResolution = null;
    this._currentResolutionBitrate = bitrate;

    this._currentPlayerWrapperInstance?.setResolutionByBitrate(bitrate);
  }

  public setAbrResolutions(resolutions: TWebRTCAbrPreset[]): void {
    this._abrResolutions = resolutions;
    this._currentPlayerWrapperInstance?.setAbrResolutions(resolutions);
  }

  public setAppKey(appKey: string): void {
    this._appKey = appKey;

    if (this._currentPlayerWrapperInstance) {
      this._currentPlayerWrapperInstance.setAppKey(appKey);
    }
  }

  public async getSupportedPlayerTypes(): Promise<EPlayerType[]> {
    if (this._supportedPlayerTypeCache) {
      return this._supportedPlayerTypeCache;
    }

    this._supportedPlayerTypeCache = (
      await Promise.all(
        AVAILABLE_PLAYER_TYPES.map(async (playerType) => ({
          playerType,
          isSupported: await playerTypeChecker[playerType](),
        })),
      )
    )
      .filter((value) => value.isSupported)
      .map(({ playerType }) => playerType);

    return this._supportedPlayerTypeCache || [];
  }

  public async setPlayerType(playerType: EPlayerType): Promise<void> {
    if (playerType === this._currentPlayerType) {
      return;
    }

    this._videoElementMetrics?.setIsPaused(playerType === EPlayerType.BROADCAST);

    if (this._videoElement && this._currentPlayerType) {
      this._videoElement.load();
    }

    const cachedPlayerType: EPlayerType | null = this._currentPlayerType;

    this._currentPlayerType = playerType;

    const supportedPlayerTypes: EPlayerType[] = await this.getSupportedPlayerTypes();

    if (!supportedPlayerTypes.includes(playerType)) {
      this._currentPlayerType = cachedPlayerType;
      throw new Error(`Player type ${playerType} is unsupported`);
    }

    this._metricsCollector?.setPlayerType(playerType);

    if (this._videoElement && playerType === EPlayerType.BROADCAST) {
      this.setVolume(0);
    }

    this._reemitStreamId();
    this._reemitCDNDomain();
    await this.recreatePlayer();

    this._eventBusInstance.publish(EPlayersEvents.PLAYER_TYPE_CHANGED, { playerType });
  }

  public async recreatePlayer(): Promise<void> {
    if (!this._currentPlayerType) {
      return;
    }

    if (this._currentPlayerWrapperInstance) {
      if (this._config.persistent) {
        this._currentPlayerWrapperInstance.stop();
      } else {
        this._currentPlayerWrapperInstance.destroy();
      }
    }

    this._currentPlayerWrapperInstance = null;

    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.MODULE_LOADING_STATE_CHANGED, {
      newState: EModuleLoadingState.LOADING,
    });

    const [wrapperConstructor] = await Promise.all([
      this._playerWrapperResolversMap[this._currentPlayerType](),
      // This is workaround for MMP React Player. We need to wait at least a little bit, otherwise React will batch updates,
      // And users of MMP won't catch moduleLoadingStateChange event
      sleep(50),
    ]);

    if (this._isDestroyed) {
      return;
    }

    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.MODULE_LOADING_STATE_CHANGED, {
      newState: EModuleLoadingState.LOADED,
    });

    if (this._playerInstancesMap[this._currentPlayerType]) {
      this._currentPlayerWrapperInstance = this._playerInstancesMap[
        this._currentPlayerType
      ] as TPossiblePlayerWrapper;
    } else {
      this._currentPlayerWrapperInstance = new wrapperConstructor(this._eventBusInstance, {
        persistent: this._config.persistent,
      });

      if (this._config.persistent) {
        this._playerInstancesMap[this._currentPlayerType] = this._currentPlayerWrapperInstance;
      }
    }

    this._retuneNewPlayerInstance();
  }

  public renderDebugPanelIntoContainer(container: HTMLDivElement): void {
    container.appendChild(this._debugPanel.rootContainer);
  }

  public start(forceRecreate: boolean = false): void {
    if (!forceRecreate && (this._hasStarted || this._isStartDelayed)) {
      return;
    }

    if (this._isCurrentConfigValid() && this._currentPlayerWrapperInstance) {
      this._isStartDelayed = false;
      this._hasStarted = true;
      if (!this._config.persistent) {
        this._currentPlayerWrapperInstance.stop();
      }
      this._currentPlayerWrapperInstance.start();
      this._reemitStreamId();
    } else {
      this._isStartDelayed = true;
    }
  }

  public stop(): void {
    this._hasStarted = false;
    this._isStartDelayed = false;

    this._currentPlayerWrapperInstance?.stop();

    this.recreatePlayer();
    this._metricsCollector?.stop();
  }

  public setVolume(volume: number): void {
    this._videoElementController?.setVolume(volume);
  }

  public setLoggerLevel(levels: TLogLevel[]): void {
    this._currentPlayerWrapperInstance?.setLoggerLevel?.(levels);
    this._videoElementController?.setLoggerLevels(this._logLevels);

    this._logLevels = levels;
  }

  public setResolutionLimits(limits: TResolutionsLimits): void {
    this._resolutionLimitsCache = limits;
    this._currentPlayerWrapperInstance?.setResolutionLimits(limits);
  }

  public setRegularBroadcastStopReason(regularBroadcastStopReason: string): void {
    this._metricsCollector?.setRegularBroadcastStopReason(regularBroadcastStopReason);
    if (
      this._currentPlayerWrapperInstance &&
      'setRegularBroadcastStopReason' in this._currentPlayerWrapperInstance &&
      typeof this._currentPlayerWrapperInstance.setRegularBroadcastStopReason === 'function'
    ) {
      this._currentPlayerWrapperInstance?.setRegularBroadcastStopReason?.(
        regularBroadcastStopReason,
      );
    }
  }

  private _retuneNewPlayerInstance(): void {
    if (!this._currentPlayerType) {
      return;
    }

    switch (this._currentPlayerType) {
      case EPlayerType.HLS:
        (this._currentPlayerWrapperInstance as DoppioWrapper)
          .setVideoElement(this._videoElement)
          .setPlayerConfig(this._config.hlsConfig || {})
          .setStreamConfig(this._currentStreamConfig)
          .recreatePlayerInstance();
        break;
      case EPlayerType.WEBRTC:
      case EPlayerType.BROADCAST:
        (this._currentPlayerWrapperInstance as RistrettoPlayerWrapper)
          .setVideoElement(this._videoElement)
          .setAbrResolutions(this._abrResolutions)
          .setPlayerConfig(this._config.webRTCConfig || {})
          .setStreamConfig(this._currentStreamConfig)
          .setAppKey(this._appKey)
          .recreatePlayerInstance();
        break;
      case EPlayerType.NATIVE:
        (this._currentPlayerWrapperInstance as NativeWrapper)
          .setVideoElement(this._videoElement)
          .setStreamConfig(this._currentStreamConfig)
          .recreatePlayerInstance();
    }

    if (this._isAbrEnabled) {
      this.enableAbr();
    } else {
      this.disableAbr();
    }

    if (this._logLevels) {
      this._currentPlayerWrapperInstance?.setLoggerLevel(this._logLevels);
      this._videoElementController?.setLoggerLevels(this._logLevels);
    }

    if (
      this._currentResolution &&
      ![EPlayerType.BROADCAST, EPlayerType.NATIVE].includes(this._currentPlayerType)
    ) {
      this._currentPlayerWrapperInstance?.setResolutionByName(this._currentResolution);
    }

    if (
      this._currentResolutionBitrate &&
      ![EPlayerType.BROADCAST, EPlayerType.NATIVE].includes(this._currentPlayerType)
    ) {
      this._currentPlayerWrapperInstance?.setResolutionByBitrate(this._currentResolutionBitrate);
    }

    if (this._metricsCollector) {
      this._currentPlayerWrapperInstance?.setMetricsCollector(this._metricsCollector);
    }

    this._currentPlayerWrapperInstance?.setResolutionLimits(this._resolutionLimitsCache);

    if (this._isStartDelayed || this._hasStarted) {
      this.start(true);
    }
  }

  private async _prepareDependencies(): Promise<void> {
    for (const playerType of [EPlayerType.HLS, EPlayerType.WEBRTC]) {
      if (this._playerInstancesMap[playerType]) {
        continue;
      }

      if (playerType === this._currentPlayerType && this._currentPlayerWrapperInstance) {
        this._playerInstancesMap[playerType] = this._currentPlayerWrapperInstance;
        continue;
      }

      const playerConstructor: TPossiblePlayerWrapperConstructor =
        await this._playerWrapperResolversMap[playerType]();

      this._playerInstancesMap[playerType] = new playerConstructor(this._eventBusInstance, {
        persistent: true,
      });
    }

    if (this._config.establishWSOnStart) {
      this._playerInstancesMap[EPlayerType.WEBRTC]?.setStreamConfig({
        WebRTCSignallingUrl: this._currentStreamConfig.WebRTCSignallingUrl,
        ...(this._currentPlayerType === EPlayerType.WEBRTC ? this._currentStreamConfig : {}),
      });
      this._playerInstancesMap[EPlayerType.WEBRTC]?.start();
    }
  }

  private _clearDependencies(): void {
    Object.values(this._playerInstancesMap).forEach((inst) => {
      if (inst !== this._currentPlayerWrapperInstance) {
        inst.destroy();
      }
    });
    this._playerInstancesMap = {};
  }

  private _reapplyPlayerConfig(): void {
    if (!this._currentPlayerWrapperInstance) {
      return;
    }
    switch (this._currentPlayerType) {
      case EPlayerType.HLS:
        if (this._config.hlsConfig) {
          (this._currentPlayerWrapperInstance as DoppioWrapper).setPlayerConfig(
            this._config.hlsConfig,
          );
        }

        break;
      case EPlayerType.BROADCAST:
      case EPlayerType.WEBRTC:
        if (this._config.webRTCConfig) {
          (this._currentPlayerWrapperInstance as RistrettoPlayerWrapper).setPlayerConfig(
            this._config.webRTCConfig,
          );
        }

        break;
      case EPlayerType.NATIVE:
        if (this._config.nativeConfig) {
          (this._currentPlayerWrapperInstance as NativeWrapper).setPlayerConfig(
            this._config.nativeConfig,
          );
        }
    }
  }

  private async _resolveDoppioWrapper(): Promise<typeof DoppioWrapper> {
    if (this._doppioWrapperConstructor) {
      return this._doppioWrapperConstructor;
    }

    this._doppioWrapperConstructor = (await import('@players/Doppio'))
      .DoppioWrapper as unknown as typeof DoppioWrapper;
    return this._doppioWrapperConstructor;
  }

  private async _resolveRistrettoWrapper(): Promise<typeof RistrettoPlayerWrapper> {
    if (this._ristrettoPlayerWrapperConstructor) {
      return this._ristrettoPlayerWrapperConstructor;
    }

    this._ristrettoPlayerWrapperConstructor = (await import('@players/RistrettoPlaying'))
      .RistrettoPlayerWrapper as unknown as typeof RistrettoPlayerWrapper;
    return this._ristrettoPlayerWrapperConstructor;
  }

  private async _resolveRistrettoBroadcasterWrapper(): Promise<typeof RistrettoBroadcastWrapper> {
    if (this._ristrettoBroadcasterWrapperConstructor) {
      return this._ristrettoBroadcasterWrapperConstructor;
    }

    this._ristrettoBroadcasterWrapperConstructor = (await import('@players/RistrettoBroadcasting'))
      .RistrettoBroadcastWrapper as unknown as typeof RistrettoBroadcastWrapper;
    return this._ristrettoBroadcasterWrapperConstructor;
  }

  private async _resolveNativeWrapper(): Promise<typeof NativeWrapper> {
    if (this._nativeWrapperConstructor) {
      return this._nativeWrapperConstructor;
    }

    this._nativeWrapperConstructor = (await import('@players/Native'))
      .NativeWrapper as unknown as typeof NativeWrapper;
    return this._nativeWrapperConstructor;
  }

  private async _emitAvailablePlayerTypes(): Promise<void> {
    const supportedPlayerTypes: EPlayerType[] = await this.getSupportedPlayerTypes();
    const availablePlayerTypesByStreamConfig: EPlayerType[] = getPlayerTypesByStreamConfig(
      this._currentStreamConfig,
    );
    const availablePlayerTypes: EPlayerType[] = supportedPlayerTypes.filter((type) =>
      availablePlayerTypesByStreamConfig.includes(type),
    );

    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.AVAILABLE_PLAYER_TYPES_UPDATED, {
      availablePlayerTypes,
    });

    this._eventBusInstance.publish(EPlayersEvents.PLAYER_TYPES_AVAILABLE, { availablePlayerTypes });
  }

  @bind
  private _handleVideoPlaybackStateChange(
    e: CustomEvent<{ prevState: EPlaybackState; nextState: EPlaybackState }>,
  ): void {
    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.VIDEO_PLAYBACK_STATE_CHANGED, e.detail);
  }

  @bind
  private _handleUpdatePlayerResolution(
    e: CustomEvent<{
      height: number;
      width: number;
      containerWidth: number;
      containerHeight: number;
    }>,
  ): void {
    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.UPDATE_PLAYER_RESOLUTION, e.detail);
  }

  @bind
  private _handleNativeEvent({
    detail: { event, eventName },
  }: TCustomEventsMap[EVideoControllerCustomEvent.NATIVE_EVENT]): void {
    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.NATIVE_EVENT, { event, eventName });
  }

  @bind
  private _handleTimeUpdate(): void {
    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.TIME_UPDATE, {});
  }

  @bind
  private _handlePlaying(e: CustomEvent<{ isAutoplay?: boolean }>): void {
    this._eventEmitter.reemitEvent(EMouflonPlayerEvent.PLAYING, e.detail);
  }

  private _isCurrentConfigValid(): boolean {
    return (
      Boolean(
        this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl,
      ) ||
      Boolean(
        this._currentPlayerType === EPlayerType.WEBRTC &&
          this._currentStreamConfig.WebRTCSignallingUrl,
      ) ||
      Boolean(
        this._currentPlayerType === EPlayerType.BROADCAST &&
          this._currentStreamConfig.WebRTCStreamId &&
          this._currentStreamConfig.WebRTCSignallingUrl,
      )
    );
  }

  private _reemitStreamId(): void {
    if (!this._hasStarted) {
      return;
    }
    if (this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl) {
      const { streamId } = parsePlaylistUrl(this._currentStreamConfig.HLSStreamUrl);
      this._metricsCollector?.setStreamId(streamId || '');
    }

    if (
      (this._currentPlayerType === EPlayerType.BROADCAST ||
        this._currentPlayerType === EPlayerType.WEBRTC) &&
      this._currentStreamConfig.WebRTCStreamId
    ) {
      this._metricsCollector?.setStreamId(this._currentStreamConfig.WebRTCStreamId);
    }

    this._reeemitRenditionSwitchMode();
  }

  private _reeemitRenditionSwitchMode(): void {
    if (this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl) {
      const { resolution } = parsePlaylistUrl(this._currentStreamConfig.HLSStreamUrl);
      if (resolution !== 'auto') {
        this._metricsCollector?.setRenditionSwitchMode('manual');
      }
    }

    if (this._metricsCollector?.currentRenditionSelectMode === 'manual') {
      this._reemitManualRendition();
    }
  }

  private _reemitManualRendition(): void {
    if (this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl) {
      const { resolution } = parsePlaylistUrl(this._currentStreamConfig.HLSStreamUrl);
      if (resolution && resolution !== 'auto') {
        this._metricsCollector?.setManualResolution(resolution);
        this._videoElementMetrics?.setUserSelectedResolution(resolution);
      }
    }
  }

  private _reemitCDNDomain(): void {
    if (this._currentPlayerType === EPlayerType.HLS && this._currentStreamConfig.HLSStreamUrl) {
      this._metricsCollector?.updateCDNUrl(this._currentStreamConfig.HLSStreamUrl);
    }

    if (
      this._currentPlayerType === EPlayerType.NATIVE &&
      this._currentStreamConfig.NativeStreamURL
    ) {
      this._metricsCollector?.updateCDNUrl(this._currentStreamConfig.NativeStreamURL);
    }

    if (
      this._currentPlayerType === EPlayerType.WEBRTC &&
      this._currentStreamConfig.WebRTCSignallingUrl
    ) {
      this._metricsCollector?.updateCDNUrl(this._currentStreamConfig.WebRTCSignallingUrl);
    }
  }

  @bind
  private _onPlayerTypeForced({
    playerType,
  }: TPlayersEventsPayload<EPlayersEvents.FORCE_PLAYER_TYPE>): void {
    this.setPlayerType(playerType);
  }

  @bind
  private _onResolutionsAvailable({
    resolutions,
  }: TPlayersEventsPayload<EPlayersEvents.RESOLUTIONS_AVAILABLE>): void {
    this._resolutionsCache = resolutions;
    this._videoElementMetrics?.setResolutions(resolutions);
  }

  @bind
  private _updateStreamConfigInternal({
    streamConfig,
  }: TPlayersEventsPayload<EPlayersEvents.STREAM_CONFIG_UPDATED_INTERNAL>): void {
    const diff: Partial<IStreamConfig> = ObjectDeepDiff(this._currentStreamConfig, streamConfig);

    const mediaStreamHasChanged: boolean =
      !!streamConfig.MediaStream !== !!this._currentStreamConfig.MediaStream ||
      streamConfig.MediaStream?.id !== this._currentStreamConfig.MediaStream?.id;

    if (!Object.keys(diff).length && !mediaStreamHasChanged) {
      return;
    }

    this._currentStreamConfig = streamConfig;

    this._eventBusInstance.publish(EPlayersEvents.STREAM_CONFIG_UPDATED, {
      streamConfig: this._currentStreamConfig,
    });

    if (this._config.establishWSOnStart && this._currentPlayerType !== EPlayerType.WEBRTC) {
      this._playerInstancesMap[EPlayerType.WEBRTC]?.setStreamConfig({
        WebRTCSignallingUrl: this._currentStreamConfig.WebRTCSignallingUrl,
      });
    }

    this._currentPlayerWrapperInstance?.setStreamConfig(this._currentStreamConfig);

    this._emitAvailablePlayerTypes();

    if (this._isStartDelayed) {
      this.start(true);
    }

    this._reemitStreamId();

    this._reemitCDNDomain();
  }

  @bind
  private _onLatencyEstimationAvailable({
    medianLatency,
  }: TPlayersEventsPayload<EPlayersEvents.LATENCY_ESTIMATION_AVAILABLE>): void {
    if (medianLatency > 0) {
      this._metricsCollector?.registerMetrics({
        latency: medianLatency,
      });
    }
  }

  get debugPanelRootContainer(): HTMLDivElement {
    return this._debugPanel.rootContainer;
  }

  get isStarted(): boolean {
    return this._hasStarted || this._isStartDelayed;
  }
}
